// src/pages/BidTemplate/BidTemplatePage.jsx
//
// Patches both apiService (bid-scenarios, engagements) and documentsApi
// (upload, list, extract, CRUD) when REACT_APP_USE_MOCK_API=true.
// Zero changes required in BidCalculatorPage or any sub-component.

import React from 'react';
import BidCalculatorPage from './BidTemplate';
import apiService        from '../../services/api';
import { documentsApi } from '../../hooks/document/documentsApi';

import {
  MOCK_BID_SCENARIOS,
  MOCK_BID_ENGAGEMENTS,
  MOCK_BID_COMPONENTS,
  MOCK_BID_DOCUMENTS,
  MOCK_BID_EXTRACTION_RESULTS,
  MOCK_EMPLOYEE_RATES,
} from '../../services/mockData';

const USE_MOCK = process.env.REACT_APP_USE_MOCK_API === 'true';

if (USE_MOCK) {
  const delay = (ms = 280) => new Promise((r) => setTimeout(r, ms));

  // ── In-memory stores ──────────────────────────────────────────────────────
  let scenarios = MOCK_BID_SCENARIOS.scenarios.map((s) => ({ ...s }));
  let documents = MOCK_BID_DOCUMENTS.map((d) => ({ ...d }));
  const extractions = { ...MOCK_BID_EXTRACTION_RESULTS };

  // ── Patch apiService (bid-scenarios + engagements) ────────────────────────
  const _get    = apiService.get.bind(apiService);
  const _post   = apiService.post.bind(apiService);
  const _put    = apiService.put.bind(apiService);
  const _patch  = apiService.patch?.bind(apiService);
  const _delete = apiService.delete.bind(apiService);

  apiService.get = async (url, params) => {
    await delay();

    // ── Saved scenarios list ──────────────────────────────────────────────
    if (/^\/bid-scenarios\/?\??/.test(url) || url === '/bid-scenarios/' || url === '/bid-scenarios') {
      const qs   = url.includes('?') ? new URLSearchParams(url.split('?')[1]) : new URLSearchParams();
      const type = params?.engagement_type || qs.get('engagement_type');
      const out  = type ? scenarios.filter((s) => s.engagement_type === type.toLowerCase()) : scenarios;
      return { scenarios: out, total: out.length };
    }

    // ── Saved scenario detail ─────────────────────────────────────────────
    const scenMatch = url.match(/^\/bid-scenarios\/([^/?]+)$/);
    if (scenMatch) {
      const found = scenarios.find((s) => s.id === scenMatch[1]);
      if (found) return { ...found };
      throw new Error('Scenario not found');
    }

    // ── Won engagements ────────────────────────────────────────────────────
    if (url.startsWith('/bid-engagements') || url.startsWith('/engagements/won') || url.includes('bid_status=Won')) {
      return MOCK_BID_ENGAGEMENTS;
    }

    // ── Engagement components (actual hours) ───────────────────────────────
    const compMatch = url.match(/\/engagements?\/([^/]+)\/components/);
    if (compMatch) return MOCK_BID_COMPONENTS[compMatch[1]] || { components: [] };

    // ── General engagements list ───────────────────────────────────────────
    if (url.startsWith('/engagements')) return MOCK_BID_ENGAGEMENTS;

    // ── Documents list ─────────────────────────────────────────────────────
    if (url === '/documents' || url.startsWith('/documents?')) {
      const { category, search } = params || {};
      let out = [...documents];
      if (category) out = out.filter((d) => d.category === category);
      if (search)   out = out.filter((d) => (d.display_name + d.original_name).toLowerCase().includes(search.toLowerCase()));
      return { data: { documents: out, total: out.length } };
    }

    // ── Document preview URL ───────────────────────────────────────────────
    const previewMatch = url.match(/^\/documents\/([^/]+)\/preview/);
    if (previewMatch) {
      return { data: { signedUrl: null } }; // no real PDF in mock — iframe stays blank
    }

    // ── Document download URL ──────────────────────────────────────────────
    const downloadMatch = url.match(/^\/documents\/([^/]+)\/download/);
    if (downloadMatch) {
      return { data: { downloadUrl: '#' } };
    }

    // ── Employee rates (for useRoleRateMap / Step4BuildServices) ─────────────
    // Cast a wide net — match any URL variant the endpoints.js might use
    if (
      url.includes('employee') ||
      url.includes('staff-rate') ||
      url.includes('bill-rate')
    ) {
      return MOCK_EMPLOYEE_RATES;
    }

    // ── Extraction result ──────────────────────────────────────────────────
    const extractionMatch = url.match(/^\/document-ai\/([^/]+)\/extraction/);
    if (extractionMatch) {
      const id  = extractionMatch[1];
      const res = extractions[id];
      if (res) return { data: { data: { extraction_result: res, ...res } } };
      throw new Error('No extraction result found');
    }

    return _get(url, params);
  };

  apiService.post = async (url, body) => {
    await delay();

    // ── Create scenario ────────────────────────────────────────────────────
    if (url.startsWith('/bid-scenarios')) {
      const s = {
        id:                `scen-${Date.now()}`,
        name:              body.name             || 'New Bid',
        client_name:       body.clientName       || '',
        engagement_type:   body.engagementType   || 'retainer',
        bid_value:         body.calculations?.bidValue   || 0,
        min_bid:           body.calculations?.minBid     || 0,
        max_bid:           body.calculations?.maxBid     || 0,
        total_hours:       body.calculations?.totalHours || 0,
        total_cost:        body.calculations?.totalCost  || 0,
        margin:            body.margin            || null,
        notes:             body.description       || null,
        selected_template: body.selectedTemplate  || null,
        selected_baseline: body.selectedBaseline  || null,
        rate_overrides:    body.rateOverrides     || {},
        services:          body.services          || [],
        updated_at:        new Date().toISOString(),
      };
      scenarios = [s, ...scenarios];
      return s;
    }

    // ── AI extraction ──────────────────────────────────────────────────────
    const extractMatch = url.match(/^\/document-ai\/([^/]+)\/extract/);
    if (extractMatch) {
      await delay(1800); // simulate Gemini processing time
      const id  = extractMatch[1];
      const doc = documents.find((d) => d.id === id);
      if (!doc) throw new Error('Document not found');

      // Return existing result if available, otherwise generate a generic one
      const stored = extractions[id];
      const result = stored || {
        status:           'success',
        confidence_score: 0.78,
        extraction: {
          client_name:     doc.client_name || doc.display_name?.split('—')[0]?.trim() || null,
          engagement_type: doc.engagement_type || 'Retainer',
          total_fee:       doc.bid_amount || null,
          monthly_fee:     doc.bid_amount || null,
          scope_summary:   'Extracted from document. Scope includes marketing services as described in the proposal.',
          services:        ['Marketing Services'],
          term_months:     12,
          industry:        null,
        },
        tags: { tags: doc.category ? [doc.category] : [] },
      };

      // Store it so "view extraction" works afterwards
      extractions[id] = result;

      // Update the document in memory
      documents = documents.map((d) =>
        d.id === id
          ? {
              ...d,
              extraction_status: result.status,
              confidence_score:  result.confidence_score,
              client_name:       result.extraction?.client_name     ?? d.client_name,
              bid_amount:        result.extraction?.total_fee       ?? d.bid_amount,
              extracted_tags:    result.tags?.tags                  ?? [],
              extracted_at:      new Date().toISOString(),
            }
          : d
      );

      return { data: { data: { extraction_result: result, ...result } } };
    }

    return _post(url, body);
  };

  apiService.put = async (url, body) => {
    await delay();
    const match = url.match(/^\/bid-scenarios\/([^/?]+)$/);
    if (match) {
      scenarios = scenarios.map((s) =>
        s.id === match[1] ? { ...s, ...body, updated_at: new Date().toISOString() } : s
      );
      return scenarios.find((s) => s.id === match[1]);
    }
    return _put(url, body);
  };

  if (_patch) {
    apiService.patch = async (url, body) => {
      await delay(150);

      // ── Update document metadata ─────────────────────────────────────────
      const docMatch = url.match(/^\/documents\/([^/?]+)$/);
      if (docMatch) {
        const id = docMatch[1];
        documents = documents.map((d) =>
          d.id === id
            ? {
                ...d,
                display_name:    body.display_name    ?? d.display_name,
                category:        body.category         ?? d.category,
                tags:            body.tags             ?? d.tags,
                bid_status:      body.bid_status       ?? d.bid_status,
                engagement_type: body.engagement_type  ?? d.engagement_type,
                engagement_id:   body.engagement_id    ?? d.engagement_id,
              }
            : d
        );
        const updated = documents.find((d) => d.id === id);
        return { data: updated };
      }

      return _patch(url, body);
    };
  }

  apiService.delete = async (url) => {
    await delay();

    // ── Delete scenario ────────────────────────────────────────────────────
    const scenMatch = url.match(/^\/bid-scenarios\/([^/?]+)$/);
    if (scenMatch) {
      scenarios = scenarios.filter((s) => s.id !== scenMatch[1]);
      return { ok: true };
    }

    // ── Delete document ────────────────────────────────────────────────────
    const docMatch = url.match(/^\/documents\/([^/?]+)$/);
    if (docMatch) {
      documents = documents.filter((d) => d.id !== docMatch[1]);
      return { ok: true };
    }

    return _delete(url);
  };

  // ── Patch ALL documentsApi methods ───────────────────────────────────────
  // Patch every method so no real API calls are made for documents,
  // regardless of whether they use apiService or raw fetch internally.

  documentsApi.listDocuments = async ({ category, search } = {}) => {
    await delay();
    let out = [...documents];
    if (category) out = out.filter((d) => d.category === category);
    if (search)   out = out.filter((d) => (d.display_name + d.original_name).toLowerCase().includes(search.toLowerCase()));
    // Return shape matches useDocuments selectDocuments: response.data.documents
    return { data: { documents: out, total: out.length } };
  };

  documentsApi.uploadDocument = async (file, displayName, category) => {
    await delay(1200);
    const newDoc = {
      id:                `doc-${Date.now()}`,
      original_name:    file.name,
      display_name:     displayName || file.name.replace(/\.[^/.]+$/, ''),
      type:             file.type,
      size:             file.size,
      uploaded_at:      new Date().toISOString(),
      category:         category || 'Other',
      bid_status:       'Pending',
      engagement_type:  'Project',
      engagement_id:    null,
      engagement_name:  null,
      client_name:      null,
      bid_amount:       null,
      extraction_status: null,
      confidence_score: null,
      extracted_tags:   [],
      extracted_at:     null,
      was_re_extracted: false,
      tags:             [],
    };
    documents = [newDoc, ...documents];
    return { id: newDoc.id, ...newDoc };
  };

  documentsApi.updateDocument = async (documentId, displayName, category, tags, bidStatus, engagementType, engagementId) => {
    await delay(150);
    documents = documents.map((d) =>
      d.id === documentId
        ? { ...d, display_name: displayName ?? d.display_name, category: category ?? d.category, tags: tags ?? d.tags, bid_status: bidStatus ?? d.bid_status, engagement_type: engagementType ?? d.engagement_type, engagement_id: engagementId ?? d.engagement_id }
        : d
    );
    return { data: documents.find((d) => d.id === documentId) };
  };

  documentsApi.deleteDocument = async (documentId) => {
    await delay();
    documents = documents.filter((d) => d.id !== documentId);
    return { ok: true };
  };

  documentsApi.getPreviewUrl = async (documentId) => {
    await delay(200);
    return { data: { signedUrl: null } }; // no real PDF in mock
  };

  documentsApi.getDownloadUrl = async (documentId) => {
    await delay(100);
    return { data: { downloadUrl: '#' } };
  };

  documentsApi.extractProposal = async (documentId, engagementId = null) => {
    await delay(1800); // simulate Gemini processing
    const doc = documents.find((d) => d.id === documentId);
    if (!doc) throw new Error('Document not found');

    const stored = extractions[documentId];
    const result = stored || {
      status:           'success',
      confidence_score: 0.78,
      extraction: {
        client_name:     doc.client_name || doc.display_name?.split('—')[0]?.trim() || null,
        engagement_type: doc.engagement_type || 'Retainer',
        total_fee:       doc.bid_amount || null,
        monthly_fee:     doc.bid_amount || null,
        scope_summary:   'Extracted from document. Scope includes marketing services as described in the proposal.',
        services:        ['Marketing Services'],
        term_months:     12,
        industry:        null,
      },
      tags: { tags: doc.category ? [doc.category] : [] },
    };

    extractions[documentId] = result;

    documents = documents.map((d) =>
      d.id === documentId
        ? { ...d, extraction_status: result.status, confidence_score: result.confidence_score, client_name: result.extraction?.client_name ?? d.client_name, bid_amount: result.extraction?.total_fee ?? d.bid_amount, extracted_tags: result.tags?.tags ?? [], extracted_at: new Date().toISOString() }
        : d
    );

    return { data: { data: { extraction_result: result, ...result } } };
  };

  documentsApi.getExtractionResult = async (documentId) => {
    await delay(100);
    const res = extractions[documentId];
    if (res) return { data: { data: { extraction_result: res, ...res } } };
    throw new Error('No extraction result found');
  };
}

// ── Page wrapper ──────────────────────────────────────────────────────────────

const BidTemplatePage = () => <BidCalculatorPage />;

export default BidTemplatePage;